import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function HomepageEditor() {
  const { API, authHeader } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site/settings`).then(r => setSettings(r.data));
  }, [API]);

  if (!settings) return <p className="text-sm text-[#5C5454]">Loading…</p>;

  const setHero = (idx, url) => {
    const next = [...settings.hero_images];
    next[idx] = url;
    setSettings({ ...settings, hero_images: next });
  };

  const clearHero = (idx) => {
    const next = [...settings.hero_images];
    next[idx] = "";
    setSettings({ ...settings, hero_images: next });
  };

  const setCategory = (key, url) => {
    setSettings({
      ...settings,
      category_images: { ...settings.category_images, [key]: url },
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/site/settings`,
        settings,
        { headers: authHeader() }
      );
      setSettings(data);
      toast.success("Homepage updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const HeroSlot = ({ idx, label }) => (
    <div className="border border-[#E5DFD3] p-4">
      <div className="label-eyebrow mb-3">{label}</div>
      <SingleUploader
        url={settings.hero_images[idx]}
        onChange={(u) => setHero(idx, u)}
        onClear={() => clearHero(idx)}
        testIdPrefix={`hero-${idx}`}
      />
    </div>
  );

  return (
    <div data-testid="homepage-editor" className="space-y-10">
      <div>
        <h2 className="font-display text-3xl mb-2">Hero collage</h2>
        <p className="text-sm text-[#5C5454] mb-6">
          The three photos at the top of the homepage. Slot 1 is the large left image; Slots 2 &amp; 3 are the smaller square stack on the right.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <HeroSlot idx={0} label="Slot 1 · Large (portrait)" />
          <HeroSlot idx={1} label="Slot 2 · Top square" />
          <HeroSlot idx={2} label="Slot 3 · Bottom square" />
        </div>
      </div>

      <div>
        <h2 className="font-display text-3xl mb-2">Category cards</h2>
        <p className="text-sm text-[#5C5454] mb-6">
          The two tiles in the "Shop by craft" section further down the homepage.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[#E5DFD3] p-4">
            <div className="label-eyebrow mb-3">Kurtis card</div>
            <SingleUploader
              url={settings.category_images.kurti}
              onChange={(u) => setCategory("kurti", u)}
              onClear={() => setCategory("kurti", "")}
              testIdPrefix="cat-kurti"
            />
          </div>
          <div className="border border-[#E5DFD3] p-4">
            <div className="label-eyebrow mb-3">Co-ord Sets card</div>
            <SingleUploader
              url={settings.category_images["co-ord"]}
              onChange={(u) => setCategory("co-ord", u)}
              onClear={() => setCategory("co-ord", "")}
              testIdPrefix="cat-coord"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={busy}
          data-testid="homepage-save"
          className="bg-[#6B1A24] text-[#FAFAF7] px-8 py-3 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save homepage"}
        </button>
        <span className="text-xs text-[#5C5454]">Changes are live immediately. No deploy needed.</span>
      </div>
    </div>
  );
}

// Single-image uploader: shows current image with a "Replace" tile; uploads via /api/uploads/image.
function SingleUploader({ url, onChange, onClear, testIdPrefix }) {
  const { API, authHeader } = useAuth();
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const toSrc = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads/image`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="relative aspect-[3/4] bg-[#F3EFEA]" data-testid={`${testIdPrefix}-preview`}>
        {url ? (
          <img src={toSrc(url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs uppercase tracking-[0.25em] text-[#5C5454]">No image</div>
        )}
      </div>
      <label className="block">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          disabled={busy}
          data-testid={`${testIdPrefix}-input`}
          className="hidden"
        />
        <span
          data-testid={`${testIdPrefix}-upload-btn`}
          className={`block text-center border border-[#2D2626] py-3 text-[10px] uppercase tracking-[0.25em] cursor-pointer hover:bg-[#2D2626] hover:text-[#FAFAF7] transition-colors ${busy ? "opacity-60 pointer-events-none" : ""}`}
        >
          {busy ? "Uploading…" : url ? "Replace image" : "Upload image"}
        </span>
      </label>
      {url && (
        <button
          type="button"
          onClick={onClear}
          data-testid={`${testIdPrefix}-clear`}
          className="text-[10px] uppercase tracking-[0.25em] text-[#6B1A24] hover:underline"
        >
          Reset to default
        </button>
      )}
    </div>
  );
}
